/**
 * useConfirm — hook reutilizável que substitui window.confirm() por AlertDialog shadcn.
 *
 * Uso:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   // dentro do JSX: <ConfirmDialog />
 *   // numa ação:
 *   if (await confirm({ title: 'Excluir?', description: '...' })) { ... }
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

export const useConfirm = () => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({
      title: options.title || 'Confirmar?',
      description: options.description || '',
      confirmLabel: options.confirmLabel || 'Confirmar',
      cancelLabel: options.cancelLabel || 'Cancelar',
      destructive: !!options.destructive,
    });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handle = (value) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  const ConfirmDialog = () => (
    <AlertDialog open={open} onOpenChange={(o) => !o && handle(false)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{opts.title}</AlertDialogTitle>
          {opts.description && (
            <AlertDialogDescription className="whitespace-pre-wrap">{opts.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handle(false)} data-testid="confirm-cancel">{opts.cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handle(true)}
            className={opts.destructive ? 'bg-red-600 hover:bg-red-700' : ''}
            data-testid="confirm-confirm"
          >
            {opts.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, ConfirmDialog };
};
